import React, { useState, useMemo, useEffect, } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, FormGroup, Label, Input, } from 'reactstrap';
import { TextDecoder, } from 'text-encoding';
import { parse as parseCsv, } from 'papaparse';
import { isEmpty, fromPairs, chunk, isFunction, } from 'lodash';
import { toast } from 'react-toastify';

import { readFile } from '../../util';
import { db } from '../../firebase';
import { invertedJas } from '../../shared/texts';

const { entries } = Object;

export default function ImportMappingModal({
  isOpen,
  toggle,
  fields = {},
  onImport,
  encoding = 'Shift_JIS',
  storageKey,
}) {
  const [file, setFile] = useState(null);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [csvRows, setCsvRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [isImporting, setIsImporting] = useState(false);

  const fieldEntries = useMemo(_ => entries(fields), [fields]);

  useEffect(_ => {
    if (isOpen && storageKey) {
      const savedMapping = localStorage.getItem(storageKey);
      if (savedMapping) {
        try {
          setMapping(JSON.parse(savedMapping));
        } catch (e) {
          console.error('Failed to parse saved mapping', e);
        }
      }
    }
  }, [isOpen, storageKey]);

  const onSelectFile = async (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) return;

    setFile(selectedFile);

    try {
      const arrayBuffer = await readFile(selectedFile, 'readAsArrayBuffer');
      let fileContent;
      let detectedEncoding;

      try {
        const utf8Decoder = new TextDecoder('UTF-8', { fatal: true });
        fileContent = utf8Decoder.decode(arrayBuffer);
        detectedEncoding = 'UTF-8';
      } catch (e) {
        const sjisDecoder = new TextDecoder(encoding);
        fileContent = sjisDecoder.decode(arrayBuffer);
        detectedEncoding = encoding;
      }

      const { data: rows } = parseCsv(fileContent, { header: true });

      if (rows.length > 0) {
        const headers = Object.keys(rows[0]);
        setCsvHeaders(headers);
        setCsvRows(rows.filter(_ => !Object.values(_).every(isEmpty)));

        const autoMapping = fromPairs(
          fieldEntries.map(([fieldName, fieldConfig]) => {
            const matchedHeader = headers.find(header =>
              header === fieldName || header === fieldConfig.label || invertedJas[header] === fieldName
            );
            return [fieldName, matchedHeader || ''];
          })
        );
        setMapping(x => ({ ...autoMapping, ...x }));
      }
    } catch (e) {
      console.error('Failed to read CSV file', e);
      toast.error('CSVファイルの読み込みに失敗しました');
    }
  };

  const updateMapping = (fieldName, csvColumn) => {
    setMapping(x => ({ ...x, [fieldName]: csvColumn }));
  };

  // Excel で保存すると数値に桁区切りカンマが付き "1,069" のようになる
  const normalizeValue = (value, fieldConfig) =>
    ['integer', 'float'].includes(fieldConfig?.type) && typeof value === 'string' ? value.replace(/,/g, '') : value;

  const validateValue = (value, fieldConfig) => {
    if (fieldConfig.validations?.required && (value == null || value === '')) {
      return ['必須項目です'];
    }

    if (value == null || value === '') {
      return [];
    }

    switch (fieldConfig.type) {
      case 'integer':
        if (!/^-?\d+$/.test(String(value).trim())) {
          return ['整数を入力してください'];
        }
        break;
      case 'float':
        if (isNaN(parseFloat(value))) {
          return ['数値を入力してください'];
        }
        break;
      case 'date':
        if (isNaN(Date.parse(value))) {
          return ['日付形式が正しくありません'];
        }
        break;
      case 'select':
        if (fieldConfig.options && !fieldConfig.options.includes(value)) {
          return [`選択肢に存在しません: ${value}`];
        }
        break;
    }

    // required 以外のカスタムバリデーション（値の書式チェックなど）も実行する
    const customErrors = entries(fieldConfig.validations || {})
      .filter(([key, validate]) => key !== 'required' && isFunction(validate) && !validate(value))
      .map(([key]) => `入力形式が正しくありません (${key})`);

    return customErrors;
  };

  const handleImport = async () => {
    const allFieldsMapped = fieldEntries
      .filter(([, fieldConfig]) => fieldConfig.validations?.required)
      .every(([fieldName]) => mapping[fieldName]);

    if (!allFieldsMapped) {
      toast.error('必須項目をすべてマッピングしてください');
      return;
    }

    setIsImporting(true);

    try {
      const mappedRows = csvRows.map(_ =>
        fromPairs(
          entries(mapping)
            .filter(([, csvColumn]) => csvColumn)
            .map(([fieldName, csvColumn]) => [fieldName, normalizeValue(_[csvColumn], fields[fieldName])])
        )
      );

      const validationErrors = mappedRows.flatMap((row, rowIndex) =>
        fieldEntries.flatMap(([fieldName, fieldConfig]) =>
          validateValue(row[fieldName], fieldConfig).map(error =>
            `行${rowIndex + 1}: ${fieldConfig.label} - ${error}`
          )
        )
      );

      if (validationErrors.length > 0) {
        toast.error(`バリデーションエラー:\n${validationErrors.slice(0, 5).join('\n')}${validationErrors.length > 5 ? `\n...他${validationErrors.length - 5}件` : ''}`);
        setIsImporting(false);
        return;
      }

      if (storageKey) {
        localStorage.setItem(storageKey, JSON.stringify(mapping));
      }

      await onImport(mappedRows);
      toast.success('インポートしました');

      setFile(null);
      setCsvHeaders([]);
      setCsvRows([]);
      toggle();
    } catch (e) {
      console.error('Import failed', e);
      toast.error('インポートに失敗しました');
    }

    setIsImporting(false);
  };

  const handleClose = () => {
    setFile(null);
    setCsvHeaders([]);
    setCsvRows([]);
    toggle();
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="lg">
      <ModalHeader>CSVインポート</ModalHeader>
      <ModalBody>
        <FormGroup>
          <Label>CSVファイルを選択</Label>
          <div>
            <Input type="file" accept=".csv" onChange={onSelectFile} />
          </div>
        </FormGroup>

        {csvHeaders.length > 0 && (
          <div className="mt-4">
            <h6>列のマッピング</h6>
            <p className="text-muted small">
              各項目に対応するCSVの列を選択してください
            </p>
            {fieldEntries.map(([fieldName, fieldConfig]) => {
              const isRequired = fieldConfig.validations?.required;
              return (
                <FormGroup key={fieldName}>
                  <Label>
                    {fieldConfig.label}
                    {isRequired && <span className="text-danger ms-1">*</span>}
                  </Label>
                  <Input
                    type="select"
                    value={mapping[fieldName] || ''}
                    onChange={e => updateMapping(fieldName, e.target.value)}
                  >
                    <option value="">選択してください</option>
                    {csvHeaders.map(_ => (
                      <option key={_} value={_}>{_}</option>
                    ))}
                  </Input>
                </FormGroup>
              );
            })}
            <div className="mt-3">
              <h6>プレビュー</h6>
              <p className="text-muted small">
                {csvRows.length}行のデータ（最大5行まで表示）
              </p>
              <div className="table-responsive">
                <table className="table table-sm table-bordered">
                  <thead className="table-light">
                    <tr>
                      {fieldEntries.map(([fieldName, fieldConfig]) => (
                        <th key={fieldName}>{fieldConfig.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {csvRows.slice(0, 5).map((row, index) => (
                      <tr key={index}>
                        {fieldEntries.map(([fieldName]) => {
                          const csvColumn = mapping[fieldName];
                          const value = csvColumn ? row[csvColumn] : '';
                          return (
                            <td key={fieldName} className="small">
                              {value || <span className="text-muted">-</span>}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={handleClose} disabled={isImporting}>
          キャンセル
        </Button>
        <Button
          color="primary"
          onClick={handleImport}
          disabled={!file || csvHeaders.length === 0 || isImporting}
        >
          {isImporting ? 'インポート中...' : 'インポート'}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
